import React, { useState, useEffect, useMemo, useRef } from "react";
import { useAppSelector } from "../../store/hooks";
import { selectIsAdmin } from "../../store/authSelectors";
import { useParams } from "react-router-dom";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { useNavigate } from "react-router-dom";
import { License } from "../../store/types";
import { useUsersAPI } from "../../hooks/api/use-usersAPI";
import { useCompaniesAPI } from "../../hooks/api/use-companiesAPI";
import { useProjectsAPI } from "../../hooks/api/use-projectsAPI";
import { useLicensesAPI } from "../../hooks/api/use-licensesAPI";
import { useGetQuery } from "../../store/slices/apiSlice";
import { formatPhone } from "../../hooks/validation";
import { getLicenseType } from "../../hooks/useLicenseType";
import {
  Container,
  ContainerXScroll,
} from "../../components/Container/Container";
import Card from "../../components/Card/Card";
import CustomInputText from "../../components/CustomInput/CustomInputText";
import { StyledIconLine } from "../../components/StyledIcon/StyledIcon";
import CustomDatatable from "../../components/CustomDatatable/CustomDatatable";
import {
  userLicenseColumns,
  getLicenseContextMenuItems,
  getLicenseMobileActions,
} from "../../components/DatatableTemplates/LicenseTableTemplates";
import AddLicenseDialogButton from "../../components/DialogButton/AddLicenseDialogButton";
import UserDialogs from "../../components/Dialogs/UserDialogs";
import LicenseDialogs from "../../components/Dialogs/LicenseDialogs";
import { H2 } from "../../styles/Typography";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import {
  IconUserFilled,
  IconCompanyFilled,
  IconMailFilled,
  IconPhoneFilled,
  IconDocFilled,
  IconDocCheckFilled,
  IconDocWarnFilled,
  IconDocErrorFilled,
  IconArrowLeft,
  IconSearch,
  IconEdit,
  IconDelete,
} from "../../assets/icons/Icon";
import {
  ContainerFlex,
  ContainerColumnFlex,
  CardsColumnContainer,
  FlexColumn,
  CardsFlex,
  ButtonContainer,
  ButtonLabel,
  SearchContainer,
} from "./UserDetails.styles";
import {
  FlexLineJustify,
  FlexLineGapSmall,
  PageName,
  Label,
  ButtonBox,
} from "../../styles/PageLayoutStyles.styles";
import { Spinner } from "../CRUDPages/CRUDPages.styles";

const UserDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)") || false;
  const isAdmin = useAppSelector(selectIsAdmin);

  const tableRef = useRef<DataTable<License[]>>(null);
  const [searchValue, setSearchValue] = useState<string>("");
  const [licenseList, setLiscenseList] = useState<License[]>([]);
  const [totalLicenses, setTotalLicenses] = useState(0);
  const [activeLicenses, setActiveLicenses] = useState(0);
  const [expiringLicenses, setExpiringLicenses] = useState(0);
  const [inactiveLicenses, setInactiveLicenses] = useState(0);
  const [selectedLicense, setSelectedLicense] = useState<License | null>(null);
  const [userDialogVisible, setUserDialogVisible] = useState<boolean>(false);
  const [userDialogType, setUserDialogType] = useState<string>("");
  const [licenseDialogVisible, setLicenseDialogVisible] =
    useState<boolean>(false);
  const [licenseDialogType, setLicenseDialogType] = useState<string>("");

  const {
    usersEP,
    meEP,
    users,
    handleUpdateUser,
    handleDeleteUser,
    toastRef: userToastRef,
    refetchData,
  } = useUsersAPI();
  const { companiesEP, companiesDropdownOpts } = useCompaniesAPI();
  const { projects, projectsDropdownOpts } = useProjectsAPI();
  const {
    licensesEP,
    handlePostLicense,
    handleActivateLicense,
    handleDeactivateLicense,
    handleUpdateLicense,
    handleUpdateLicenseUser,
    handleDeleteLicenseUser,
    handleDeleteLicense,
    toastRef,
  } = useLicensesAPI();

  const userEndpoint = isAdmin ? `${usersEP}/${id}` : meEP;
  const licensesEndpoint = isAdmin
    ? `${usersEP}/${id}/licenses`
    : `${meEP}/licenses`;

  const {
    data: user,
    refetch: refetchUser,
    isLoading,
  } = useGetQuery(userEndpoint, {
    refetchOnMountOrArgChange: true,
    skip: !id || !usersEP || !meEP,
  });

  const { data: licenses, refetch: refetchLicenses } = useGetQuery(
    licensesEndpoint,
    {
      refetchOnMountOrArgChange: true,
      skip: !id || !usersEP || !meEP,
    }
  );

  const { data: company, refetch: refetchCompany } = useGetQuery(
    `${companiesEP}/${user?.organizationId}`,
    {
      refetchOnMountOrArgChange: true,
      skip: !id || !companiesEP || !user || !isAdmin,
    }
  );

  useEffect(() => {
    if (licenses) {
      const enriched = licenses.map((license: License | any) => {
        let companyName = "";
        if (company) {
          companyName = company?.name;
        } else {
          companyName = user?.organizationName;
        }
        const licenseType = getLicenseType(license.type);

        let projectName = "";
        if (projects) {
          projectName =
            projects?.find((p) => p.id === license.projectId)?.name ??
            license.projectId;
        } else {
          projectName = license?.projectName;
        }

        let validityDays = 0;
        if (license.startDate && license.expirationDate) {
          const start = new Date(license.startDate);
          const end = new Date(license.expirationDate);
          validityDays = Math.max(
            0,
            (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
          );
        }

        let remainingDays = 0;
        if (license.expirationDate) {
          const now = new Date();
          const exp = new Date(license.expirationDate);
          remainingDays =
            (exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
        }

        return {
          ...license,
          licenseType,
          companyName,
          projectName,
          _validityDays: validityDays,
          _timeRemainingDays: remainingDays,
        };
      });
      setLiscenseList(enriched);

      const now = new Date();
      let total = licenses.length;
      let active = 0;
      let expiring = 0;
      let inactive = 0;

      licenses.forEach((license: License) => {
        const isActive = license.isEnabled;
        const expDate = license.expirationDate
          ? new Date(license.expirationDate)
          : null;
        const isExpired = expDate && expDate < now;
        let daysRemaining = expDate
          ? (expDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
          : Infinity;

        if (isActive && !isExpired) {
          active++;
          if (daysRemaining < 30) expiring++;
        } else {
          inactive++;
        }
      });

      setTotalLicenses(total);
      setActiveLicenses(active);
      setExpiringLicenses(expiring);
      setInactiveLicenses(inactive);
    } else {
      setLiscenseList([]);
      setTotalLicenses(0);
      setActiveLicenses(0);
      setExpiringLicenses(0);
      setInactiveLicenses(0);
    }
  }, [licenses, company, projects]);

  const filteredData = useMemo(() => {
    if (!searchValue.trim() || !licenseList) return licenseList;
    const lowerSearch = searchValue.toLowerCase();
    return licenseList.filter((license) => {
      const lic = license as any;
      return (
        lic.projectName?.toLowerCase().includes(lowerSearch) ||
        lic.licenseType?.toLowerCase().includes(lowerSearch) ||
        lic.isEnabled?.toString().includes(lowerSearch) ||
        lic.startDate?.toString().includes(lowerSearch) ||
        lic.expirationDate?.toString().includes(lowerSearch) ||
        lic.validityPeriod?.toLowerCase().includes(lowerSearch) ||
        lic.timeRemaining?.toLowerCase().includes(lowerSearch)
      );
    });
  }, [licenseList, searchValue]);

  const handleEnableLicense = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=enable&id=${license.id}`);
    } else {
      setLicenseDialogType("enable");
      setLicenseDialogVisible(true);
    }
  };

  const handleDisableLicense = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=disable&id=${license.id}`);
    } else {
      setLicenseDialogType("disable");
      setLicenseDialogVisible(true);
    }
  };

  const handleAddUser = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=addUser&id=${license.id}`);
    } else {
      setLicenseDialogType("addUser");
      setLicenseDialogVisible(true);
    }
  };

  const editLicenseUser = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=editUser&id=${license.id}`);
    } else {
      setLicenseDialogType("editUser");
      setLicenseDialogVisible(true);
    }
  };

  const deleteLicenseUser = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=deleteUser&id=${license.id}`);
    } else {
      setLicenseDialogType("deleteUser");
      setLicenseDialogVisible(true);
    }
  };

  const contextMenuItems = useMemo(
    () =>
      getLicenseContextMenuItems(
        selectedLicense,
        handleAddUser,
        editLicenseUser,
        deleteLicenseUser,
        handleEnableLicense,
        handleDisableLicense
      ),
    [selectedLicense, handleAddUser, handleEnableLicense, handleDisableLicense]
  );

  const mobileActions = useMemo(
    () => getLicenseMobileActions(handleEnableLicense, handleDisableLicense),
    [handleEnableLicense, handleDisableLicense]
  );

  const handleView = (license: License) => {
    navigate(`/licenses/${license.id}`);
  };

  const handleSelectionChange = (selected: License | null) => {
    setSelectedLicense(selected);
  };

  const resetAllFilters = () => {
    if (tableRef.current) {
      tableRef.current.reset();
    }
    setSearchValue("");
  };

  const editLicense = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=edit&id=${license.id}`);
    } else {
      setLicenseDialogType("edit");
      setLicenseDialogVisible(true);
    }
  };

  const deleteLicense = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=delete&id=${license.id}`);
    } else {
      setLicenseDialogType("delete");
      setLicenseDialogVisible(true);
    }
  };

  const editUser = () => {
    if (isMobile) {
      navigate(`/users/form?mode=edit&id=${user.id}`);
    } else {
      setUserDialogType("edit");
      setUserDialogVisible(true);
    }
  };

  const deleteUser = () => {
    if (isMobile) {
      navigate(`/users/form?mode=delete&id=${user.id}`);
    } else {
      setUserDialogType("delete");
      setUserDialogVisible(true);
    }
  };

  const deleteUserAPI = async (): Promise<boolean> => {
    try {
      let success = await handleDeleteUser(user);
      if (success) {
        return true;
      }
      return false;
    } catch (error) {
      return false;
    } finally {
      setTimeout(() => {
        navigate(-1);
      }, 2000);
      refetchData();
    }
  };

  if (isLoading)
    return (
      <Container>
        <Spinner>
          <ProgressSpinner />
          <p>Загрузка...</p>
        </Spinner>
      </Container>
    );

  return (
    <ContainerFlex>
      <Toast ref={toastRef} position="bottom-center" />
      <Toast ref={userToastRef} position="bottom-center" />
      <ContainerColumnFlex>
        <CardsColumnContainer>
          <Container>
            <FlexLineJustify>
              <H2>Данные пользователя</H2>
              {isAdmin && (
                <ButtonBox>
                  <Button text onClick={editUser}>
                    <IconEdit size={16} />
                  </Button>
                  <Button text onClick={deleteUser}>
                    <IconDelete size={16} />
                  </Button>
                </ButtonBox>
              )}
            </FlexLineJustify>
            <FlexColumn>
              <StyledIconLine
                icon={<IconUserFilled size={20} />}
                text={
                  user
                    ? `${user.lastName ?? ""} ${user.name ?? ""} ${
                        user.patronymic ?? ""
                      }`
                    : ""
                }
              />
              <StyledIconLine
                icon={<IconCompanyFilled size={20} />}
                text={user?.organizationName}
              />
              <StyledIconLine
                icon={<IconMailFilled size={20} />}
                text={user?.email}
              />
              <StyledIconLine
                icon={<IconPhoneFilled size={20} />}
                text={user.phone ? formatPhone(user!.phone) : "Нет данных"}
              />
            </FlexColumn>
          </Container>

          <Container>
            <H2>Статистика</H2>
            <FlexColumn>
              <CardsFlex>
                <Card
                  icon={<IconDocFilled size={24} />}
                  header={totalLicenses.toString()}
                  title="Количество лицензий"
                />
                <Card
                  icon={<IconDocCheckFilled size={24} />}
                  header={activeLicenses.toString()}
                  title="Активные лицензии"
                />
              </CardsFlex>
              <CardsFlex>
                <Card
                  icon={<IconDocWarnFilled size={24} />}
                  header={expiringLicenses.toString()}
                  title="Истекающие лицензии"
                />
                <Card
                  icon={<IconDocErrorFilled size={24} />}
                  header={inactiveLicenses.toString()}
                  title="Неактивные лицензии"
                />
              </CardsFlex>
            </FlexColumn>
          </Container>
        </CardsColumnContainer>

        {isAdmin && (
          <ButtonContainer>
            <Button outlined onClick={() => navigate(-1)}>
              <IconArrowLeft size={16} />
              <ButtonLabel>Назад</ButtonLabel>
            </Button>
          </ButtonContainer>
        )}
      </ContainerColumnFlex>

      <ContainerXScroll>
        <FlexLineJustify>
          <PageName>
            <H2>Лицензии</H2>
          </PageName>
          <FlexLineGapSmall>
            {isAdmin && (
              <AddLicenseDialogButton
                user={user}
                company={company}
                projectsOptions={projectsDropdownOpts}
                handleActivateLicense={handleActivateLicense}
                handleDeactivateLicense={handleDeactivateLicense}
                handleAddLicense={handlePostLicense}
                handleEditLicense={handleUpdateLicense}
                handleEditLicenseUser={handleUpdateLicenseUser}
                handleDeleteLicense={handleDeleteLicense}
                refetchData={refetchLicenses}
                licensesEP={licensesEP}
                isMobile={isMobile}
              />
            )}
            <Button
              icon="pi pi-filter-slash"
              onClick={resetAllFilters}
              outlined
            >
              <Label>Сбросить</Label>
            </Button>
            <SearchContainer>
              <CustomInputText
                value={searchValue}
                setValue={setSearchValue}
                placeholder="Поиск..."
                icon={<IconSearch size={20} />}
              />
            </SearchContainer>
          </FlexLineGapSmall>
        </FlexLineJustify>

        <CustomDatatable<License>
          ref={tableRef}
          data={filteredData ?? []}
          columns={userLicenseColumns}
          onView={handleView}
          onEdit={editLicense}
          onDelete={deleteLicense}
          onRowDoubleClick={isAdmin ? handleView : undefined}
          selected={selectedLicense}
          onSelectionChange={handleSelectionChange}
          getRowId={(license) => license.id}
          extraContextMenuItems={contextMenuItems}
          hideViewButton={!isAdmin}
          mobileActions={mobileActions}
          hideContextMenu={!isAdmin}
          sortable
        />
        <LicenseDialogs
          visible={licenseDialogVisible}
          onHide={() => setLicenseDialogVisible(false)}
          dialogType={licenseDialogType}
          user={user}
          usersList={users}
          license={selectedLicense!}
          enrichedLicense={{
            ...selectedLicense!,
            userName: `${user.lastName ?? ""} ${user.name ?? ""} ${
              user.patronymic ?? ""
            }`,
          }}
          handleActivateLicense={handleActivateLicense}
          handleDeactivateLicense={handleDeactivateLicense}
          handleAddLicense={handlePostLicense}
          handleEditLicense={handleUpdateLicense}
          handleEditLicenseUser={handleUpdateLicenseUser}
          handleDeleteLicenseUser={handleDeleteLicenseUser}
          handleDeleteLicense={handleDeleteLicense}
          refetchData={refetchLicenses}
          companiesOptions={companiesDropdownOpts}
          projectsOptions={projectsDropdownOpts}
          licensesEP={licensesEP}
        />
      </ContainerXScroll>

      <UserDialogs
        visible={userDialogVisible}
        onHide={() => setUserDialogVisible(false)}
        dialogType={userDialogType}
        user={user}
        handleEditUser={handleUpdateUser}
        handleDeleteUser={deleteUserAPI}
        refetchData={refetchUser}
        companiesOptions={companiesDropdownOpts}
      />
    </ContainerFlex>
  );
};

export default UserDetails;
